import type { Catalog, Episode, Media, PlaybackAccess, Title } from "../types";

export const SUPPORTED_SCHEMA = 1;

export class CatalogError extends Error {}

const SAFE_PATH = /^(?!\/)(?!.*\.\.)(?!.*\\)(?![a-zA-Z][a-zA-Z0-9+.-]*:)[A-Za-z0-9._/ -]+$/;

export function isSafePath(p: unknown): p is string {
  return typeof p === "string" && p.length > 0 && p.length <= 512 && SAFE_PATH.test(p);
}

/** Validación estructural en el navegador (la validación completa la hace el Media Manager al publicar). */
export function parseCatalog(raw: unknown): Catalog {
  if (!raw || typeof raw !== "object") throw new CatalogError("El catálogo está vacío o dañado.");
  const c = raw as Catalog;
  if (c.schemaVersion !== SUPPORTED_SCHEMA)
    throw new CatalogError(
      `El catálogo usa la versión ${String(c.schemaVersion)} y esta web solo entiende la ${SUPPORTED_SCHEMA}. Actualiza Radiant TV.`,
    );
  for (const key of ["items", "channels", "iptv", "collections"] as const)
    if (!Array.isArray(c[key])) throw new CatalogError(`Falta la lista «${key}» en el catálogo.`);
  const checkMedia = (m: Media | undefined, where: string) => {
    if (!m) return;
    const paths = [m.base, m.master, ...m.video.map((v) => v.playlist), ...m.audio.map((a) => a.playlist),
      ...m.subtitles.map((s) => s.path), ...(m.fonts ?? [])];
    if (!paths.every(isSafePath)) throw new CatalogError(`Ruta no válida en ${where}.`);
  };
  c.items = c.items.filter((t) => typeof t.id === "string" && typeof t.title === "string");
  for (const t of c.items) {
    checkMedia(t.media, t.title);
    for (const s of t.seasons ?? []) for (const e of s.episodes) checkMedia(e.media, `${t.title} ${e.id}`);
    for (const p of [t.poster, t.backdrop]) if (p !== undefined && !isSafePath(p)) throw new CatalogError(`Imagen no válida en ${t.title}.`);
  }
  c.iptv = c.iptv.filter((i) => typeof i.url === "string" && i.url.startsWith("https://"));
  return c;
}

export function assetUrl(access: PlaybackAccess | null, key: string | undefined): string | undefined {
  if (!access || !key) return undefined;
  const path = key.split("/").map(encodeURIComponent).join("/");
  const url = access.base + path;
  return access.token ? `${url}?Authorization=${encodeURIComponent(access.token)}` : url;
}

export function mediaUrl(access: PlaybackAccess | null, media: Media, rel: string): string | undefined {
  return assetUrl(access, `${media.base}/${rel}`);
}

/** Añade el token de descarga a cualquier URL que apunte a la base de B2 (segmentos, playlists, fuentes). */
export function authorize(access: PlaybackAccess | null, url: string): string {
  if (!access?.token || !url.startsWith(access.base) || /[?&]Authorization=/.test(url)) return url;
  return url + (url.includes("?") ? "&" : "?") + "Authorization=" + encodeURIComponent(access.token);
}

export function allEpisodes(t: Title): (Episode & { season: number })[] {
  return (t.seasons ?? []).flatMap((s) => s.episodes.map((e) => ({ ...e, season: s.number })));
}

export function findTitle(c: Catalog, id: string): Title | undefined {
  return c.items.find((t) => t.id === id);
}

export function findEpisode(t: Title, episodeId: string): (Episode & { season: number }) | undefined {
  return allEpisodes(t).find((e) => e.id === episodeId);
}

export function nextEpisode(t: Title, episodeId: string): (Episode & { season: number }) | undefined {
  const eps = allEpisodes(t);
  const i = eps.findIndex((e) => e.id === episodeId);
  return i >= 0 ? eps[i + 1] : undefined;
}

export function mediaFor(t: Title, episodeId?: string): Media | undefined {
  if (t.kind === "movie") return t.media;
  return episodeId ? findEpisode(t, episodeId)?.media : allEpisodes(t)[0]?.media;
}

export function runtimeLabel(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
}

const LANG_NAMES = new Intl.DisplayNames(["es"], { type: "language" });
export function languageName(code: string): string {
  if (!code || code === "und") return "Desconocido";
  if (code === "es-419") return "Español latino";
  if (code === "es-ES") return "Español de España";
  try {
    const n = LANG_NAMES.of(code) ?? code;
    return n.charAt(0).toUpperCase() + n.slice(1);
  } catch {
    return code;
  }
}

export function normalize(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

export function search(c: Catalog, q: string): Title[] {
  const terms = normalize(q).split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  return c.items
    .map((t) => {
      const hay = normalize([t.title, t.originalTitle, t.overview, ...(t.genres ?? []), String(t.year ?? ""),
        ...allEpisodes(t).map((e) => e.title)].join(" "));
      const title = normalize(t.title);
      if (!terms.every((x) => hay.includes(x))) return null;
      const score = terms.reduce((s, x) => s + (title.startsWith(x) ? 3 : title.includes(x) ? 2 : 1), 0);
      return { t, score };
    })
    .filter((x): x is { t: Title; score: number } => !!x)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.t);
}
